import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Trash2, Save, Plus, Loader2, CheckCircle2, FileText, Image as ImageIcon, X, ExternalLink } from "lucide-react";
import api, { apiErr } from "@/lib/api";

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

const emptyEdition = () => ({
  id: null,
  date: todayISO(),
  title: "",
  edition: "मुख्य संस्करण",
  files: [],
  published: true,
});

export default function EPaperAdmin() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(emptyEdition());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/epaper");
      setList(r.data || []);
    } catch (e) { setErr(apiErr(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEdit(emptyEdition()); setErr(""); setOk(""); };
  const startEdit = (d) => { setEdit({ ...d, files: [...(d.files || [])] }); setErr(""); setOk(""); };

  const uploadFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true); setErr("");
    try {
      const uploaded = [];
      for (const file of fileList) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/epaper/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ ...data, label: file.name });
      }
      setEdit((e) => ({ ...e, files: [...e.files, ...uploaded] }));
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || "अपलोड विफल");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (idx) => {
    setEdit((e) => ({ ...e, files: e.files.filter((_, i) => i !== idx) }));
  };

  const moveFile = (idx, dir) => {
    const arr = [...edit.files];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setEdit((e) => ({ ...e, files: arr }));
  };

  const save = async () => {
    if (!edit.date) { setErr("तारीख़ ज़रूरी है"); return; }
    setBusy(true); setErr(""); setOk("");
    try {
      const payload = {
        date: edit.date,
        title: edit.title,
        edition: edit.edition,
        published: edit.published,
        files: edit.files.map((f) => ({ url: f.url, path: f.path || "", kind: f.kind, ext: f.ext || "", label: f.label || "" })),
      };
      let saved;
      if (edit.id) {
        saved = (await api.put(`/epaper/${edit.id}`, payload)).data;
      } else {
        saved = (await api.post("/epaper", payload)).data;
      }
      setEdit({ ...emptyEdition(), ...saved, files: [...(saved.files || [])] });
      setOk("ई-पेपर सहेजा गया");
      setTimeout(() => setOk(""), 3000);
      await load();
    } catch (e) { setErr(apiErr(e.response?.data?.detail) || "सेव नहीं हुआ"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("इस संस्करण को हटाएँ?")) return;
    await api.delete(`/epaper/${id}`);
    if (edit.id === id) startNew();
    await load();
  };

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-serif-dev">ई-पेपर प्रबंधन</h1>
          <p className="text-sm text-gray-500 mt-1">दिनांक के साथ PDF / JPG / PNG अपलोड करें — जनता को <Link to="/epaper" className="text-[#D92525] hover:underline">/epaper</Link> पर दिखेगा।</p>
        </div>
        <button onClick={startNew} data-testid="epaper-new-btn"
          className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#D92525]">
          <Plus size={16} /> नया संस्करण
        </button>
      </div>

      {err && <div className="text-sm text-[#D92525] bg-red-50 rounded px-4 py-2.5">{err}</div>}
      {ok && <div className="text-sm text-green-700 bg-green-50 rounded px-4 py-2.5 flex items-center gap-2"><CheckCircle2 size={15} /> {ok}</div>}

      <div className="grid grid-cols-12 gap-6">
        {/* Editor */}
        <section className="col-span-12 lg:col-span-8 bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-5" data-testid="epaper-editor">
          <h2 className="text-lg font-bold text-gray-900">
            {edit.id ? "संस्करण संपादित करें" : "नया संस्करण अपलोड करें"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">तारीख़ *</span>
              <input type="date" required data-testid="epaper-date"
                value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })}
                className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">संस्करण नाम</span>
              <input data-testid="epaper-edition-name"
                value={edit.edition} onChange={(e) => setEdit({ ...edit, edition: e.target.value })}
                placeholder="मुख्य संस्करण / नर्मदापुरम / भोपाल"
                className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" />
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">शीर्षक (वैकल्पिक)</span>
            <input data-testid="epaper-title"
              value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
              placeholder="ई-पेपर · YYYY-MM-DD"
              className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" />
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} className="accent-[#D92525]" data-testid="epaper-published" />
            <span className="text-sm text-gray-700">प्रकाशित करें (जनता को दिखे)</span>
          </label>

          {/* File uploader */}
          <div className="border border-dashed border-gray-300 rounded-lg p-5 bg-gray-50">
            <div className="flex items-center gap-2 mb-3">
              <Upload size={18} className="text-[#D92525]" />
              <span className="font-semibold text-sm">फ़ाइलें अपलोड करें</span>
              <span className="text-xs text-gray-500">PDF · JPG · PNG (max 40MB / file)</span>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
              disabled={uploading}
              data-testid="epaper-file-input"
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#0F0F0F] file:text-white hover:file:bg-[#D92525]"
            />
            {uploading && <div className="mt-3 text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> अपलोड हो रहा है…</div>}
            <p className="mt-3 text-xs text-gray-500">
              टिप: पूरा अख़बार एक <b>multi-page PDF</b> में सबसे तेज़ लोड होता है। पेज-वार <b>JPG/PNG</b> अपलोड करने पर उपयोगकर्ता पेज बदल कर देख सकते हैं।
            </p>
          </div>

          {/* File list */}
          {edit.files.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">{edit.files.length} फ़ाइल जुड़ी हुई</div>
              <ul className="space-y-2">
                {edit.files.map((f, i) => (
                  <li key={i} data-testid={`epaper-file-${i}`}
                    className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    {f.kind === "pdf" ? <FileText size={16} className="text-[#D92525]" /> : <ImageIcon size={16} className="text-[#D92525]" />}
                    <a href={f.url} target="_blank" rel="noreferrer" className="text-sm text-gray-800 hover:text-[#D92525] flex-1 truncate">
                      {f.label || f.url.split("/").pop()}
                    </a>
                    <span className="text-xs text-gray-400 uppercase">{f.kind}</span>
                    <button onClick={() => moveFile(i, -1)} disabled={i === 0} className="text-xs text-gray-500 hover:text-[#D92525] disabled:opacity-30">↑</button>
                    <button onClick={() => moveFile(i, 1)} disabled={i === edit.files.length - 1} className="text-xs text-gray-500 hover:text-[#D92525] disabled:opacity-30">↓</button>
                    <button onClick={() => removeFile(i)} data-testid={`epaper-file-remove-${i}`} className="text-[#D92525] hover:opacity-70"><X size={14} /></button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button onClick={save} disabled={busy || uploading} data-testid="epaper-save"
              className="inline-flex items-center gap-2 bg-[#D92525] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0A0F1E] disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} सहेजें
            </button>
            {edit.id && (
              <a href="/epaper" target="_blank" rel="noreferrer" className="text-sm text-gray-600 hover:text-[#D92525] inline-flex items-center gap-1">
                <ExternalLink size={14} /> देखें
              </a>
            )}
          </div>
        </section>

        {/* Sidebar list */}
        <aside className="col-span-12 lg:col-span-4 bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-4">सभी संस्करण ({list.length})</h3>
          {list.length === 0 ? (
            <div className="text-sm text-gray-400 py-4 text-center">कोई संस्करण नहीं</div>
          ) : (
            <ul className="space-y-2 max-h-[500px] overflow-y-auto">
              {list.map((d) => (
                <li key={d.id} data-testid={`epaper-list-${d.date}`}
                  className={`border rounded-lg px-3 py-2.5 ${edit.id === d.id ? "border-[#D92525] bg-red-50" : "border-gray-100 hover:bg-gray-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <button onClick={() => startEdit(d)} className="text-left flex-1">
                      <div className="text-sm font-semibold text-gray-800">{d.date}</div>
                      <div className="text-xs text-gray-500">{d.edition} · {d.files?.length || 0} फ़ाइल {d.published ? "" : "· 🔒 डाफ्ट"}</div>
                    </button>
                    <button onClick={() => remove(d.id)} className="text-[#D92525] hover:opacity-70 p-1"><Trash2 size={14} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
